import { useCallback, useMemo, useState } from 'react';

import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { UseFormReturn } from 'react-hook-form';

import { FilterValues } from '@/hooks/use-filter-values';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import {
  hashItemsForStaleness,
  mergeLeafWithFormEdits,
  usePollingStaleness,
} from '@/modules/finance/common';

import { getGenerateBillsData } from '../../api';
import { getBillMemberPath } from '../../components/generate-bills-table/bill-row.helpers';
import {
  BillHoursSourceType,
  BillTimeOffSourceType,
  GenerateBillListItem,
  GenerateBillMemberProjectListItem,
  GenerateBillMemberTimeOffListItem,
  GenerateBillSubRow,
} from '../../types';
import { GenerateBillsTableFormValues } from '../../validations';
import { GENERATE_BILLS_DATA_QUERY_KEY } from './use-generate-bills-data';

const extractBillStaleFields = (item: GenerateBillListItem) => ({
  projectsCount: item.memberProjects?.length ?? 0,
  timeOffsCount: item.memberTimeOffs?.length ?? 0,
  projects: (item.memberProjects ?? []).map(p => ({
    id: p.id,
    sources: (p.hoursSources ?? []).map(s => ({
      type: s.sourceType,
      h: s.hours,
      r: s.averageRate,
      a: s.amount,
    })),
  })),
  timeOffs: (item.memberTimeOffs ?? []).map(t => ({
    id: t.timeOffTypeId,
    sources: (t.hoursSources ?? []).map(s => ({
      type: s.sourceType,
      h: s.hours,
      r: s.averageRate,
      a: s.amount,
    })),
  })),
});

const hashBillItems = (items: GenerateBillListItem[] | undefined): string =>
  hashItemsForStaleness(
    (items ?? []).map(it => ({ ...it, id: it.member.id })),
    item => extractBillStaleFields(item as unknown as GenerateBillListItem)
  );

const isAutoDerivedProjectValue = (
  value: number | undefined,
  sources: GenerateBillMemberProjectListItem['hoursSources'],
  field: 'hours' | 'averageRate'
): boolean => {
  if (value == null) return true;
  return sources.some(source => source[field] === value);
};

const mergeProject = (
  newProject: GenerateBillMemberProjectListItem,
  oldProject: GenerateBillMemberProjectListItem,
  formProject: { billBy?: unknown; hours?: number; effectiveRate?: number } | undefined
): GenerateBillMemberProjectListItem => {
  const oldSources = oldProject.hoursSources ?? [];
  const newSources = newProject.hoursSources ?? [];

  const selectedSource =
    (formProject?.billBy as BillHoursSourceType | undefined) ?? oldProject.billBy;
  const newPrimary =
    newSources.find(s => s.sourceType === selectedSource) ??
    newSources.find(s => s.sourceType === BillHoursSourceType.TRACKED) ??
    newSources[0];

  const currentHours = formProject?.hours ?? oldProject.hours;
  const currentRate = formProject?.effectiveRate ?? oldProject.effectiveRate;

  const { hours, effectiveRate, total } = mergeLeafWithFormEdits({
    newPrimaryHours: newPrimary?.hours,
    newPrimaryRate: newPrimary?.averageRate,
    newPrimaryAmount: newPrimary?.amount,
    currentHours,
    currentRate,
    hoursAutoDerived: isAutoDerivedProjectValue(currentHours, oldSources, 'hours'),
    rateAutoDerived: isAutoDerivedProjectValue(currentRate, oldSources, 'averageRate'),
  });

  return { ...newProject, billBy: selectedSource, hours, effectiveRate, total };
};

const isAutoDerivedTimeOffValue = (
  value: number | undefined,
  sources: GenerateBillMemberTimeOffListItem['hoursSources'],
  field: 'hours' | 'averageRate'
): boolean => {
  if (value == null) return true;
  return (sources ?? []).some(source => source[field] === value);
};

const mergeTimeOff = (
  newTimeOff: GenerateBillMemberTimeOffListItem,
  oldTimeOff: GenerateBillMemberTimeOffListItem,
  formTimeOff: { billBy?: unknown; hours?: number; effectiveRate?: number } | undefined
): GenerateBillMemberTimeOffListItem => {
  const oldSources = oldTimeOff.hoursSources ?? [];
  const newSources = newTimeOff.hoursSources ?? [];

  // Follow whichever approval status the user currently has selected, falling back to
  // APPROVED (then any source) if that status no longer exists in the fresh payload.
  const selectedSource =
    (formTimeOff?.billBy as BillTimeOffSourceType | undefined) ?? oldTimeOff.billBy;
  const newPrimary =
    newSources.find(s => s.sourceType === selectedSource) ??
    newSources.find(s => s.sourceType === BillTimeOffSourceType.APPROVED) ??
    newSources[0];

  const currentHours = formTimeOff?.hours ?? oldTimeOff.hours;
  const currentRate = formTimeOff?.effectiveRate ?? oldTimeOff.effectiveRate;

  const { hours, effectiveRate, total } = mergeLeafWithFormEdits({
    newPrimaryHours: newPrimary?.hours,
    newPrimaryRate: newPrimary?.averageRate,
    newPrimaryAmount: newPrimary?.amount,
    currentHours,
    currentRate,
    hoursAutoDerived: isAutoDerivedTimeOffValue(currentHours, oldSources, 'hours'),
    rateAutoDerived: isAutoDerivedTimeOffValue(currentRate, oldSources, 'averageRate'),
  });

  return {
    ...newTimeOff,
    billBy: newPrimary?.sourceType ?? selectedSource,
    hours,
    effectiveRate,
    total,
  };
};

interface UseSmartRefreshParams {
  filters: FilterValues['values'];
  apiFilters: Parameters<typeof getGenerateBillsData>[0]['filters'];
  enabled: boolean;
  /** The items currently shown (from the authoritative list query). */
  items: GenerateBillListItem[] | undefined;
}

interface UseSmartRefreshResult {
  hasStaleData: boolean;
  isRefreshing: boolean;
  applySmartRefresh: (
    form: UseFormReturn<GenerateBillsTableFormValues>,
    table: Table<GenerateBillListItem>
  ) => Promise<void>;
}

export const useGenerateBillsSmartRefresh = ({
  filters,
  apiFilters,
  enabled,
  items,
}: UseSmartRefreshParams): UseSmartRefreshResult => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const filterFingerprint = useMemo(() => JSON.stringify(filters), [filters]);
  const authoritativeQueryKey = useMemo(
    () => [GENERATE_BILLS_DATA_QUERY_KEY, filters] as const,
    [filters]
  );
  const peekQueryKey = useMemo(
    () => [GENERATE_BILLS_DATA_QUERY_KEY, filters, 'peek'] as const,
    [filters]
  );

  const { hasStaleData, latestPeekRef, markAccepted } = usePollingStaleness<GenerateBillListItem>({
    enabled,
    filterFingerprint,
    authoritativeItems: items,
    peekQueryKey,
    fetchPeek: () => getGenerateBillsData({ filters: apiFilters }),
    hashItems: hashBillItems,
  });

  const applySmartRefresh = useCallback<UseSmartRefreshResult['applySmartRefresh']>(
    async (form, table) => {
      const fresh = latestPeekRef.current;
      if (!fresh) return;

      setIsRefreshing(true);
      try {
        const cached =
          queryClient.getQueryData<InfiniteData<PaginatedResponse<GenerateBillListItem>>>(
            authoritativeQueryKey
          );
        const oldItems = cached?.pages?.flatMap(page => page.items) ?? [];
        // Match members/projects/time-offs by STABLE ID, not array position — the form is
        // id-keyed and the server may reorder or drop rows between polls.
        const oldByMember = new Map(oldItems.map(m => [m.member.id, m]));

        const mergedItems = fresh.map(newMember => {
          const oldMember = oldByMember.get(newMember.member.id);
          if (!oldMember) return newMember;
          const base = getBillMemberPath(newMember.member.id);
          const oldProjectById = new Map((oldMember.memberProjects ?? []).map(p => [p.id, p]));
          const oldTimeOffById = new Map(
            (oldMember.memberTimeOffs ?? []).map(t => [t.timeOffTypeId, t])
          );

          // `billBy` holds a project hours-source on project leaves and a time-off approval
          // status on time-off leaves; each merge function narrows it to its own enum.
          const readLeaf = (leaf: string) => ({
            billBy: form.getValues(`${leaf}.billBy` as never) as
              | BillHoursSourceType
              | BillTimeOffSourceType
              | undefined,
            hours: form.getValues(`${leaf}.hours` as never) as number | undefined,
            effectiveRate: form.getValues(`${leaf}.effectiveRate` as never) as number | undefined,
          });
          const writeLeaf = (leaf: string, hours: number, effectiveRate: number) => {
            form.setValue(`${leaf}.hours` as never, hours as never, {
              shouldDirty: false,
              shouldValidate: false,
            });
            form.setValue(`${leaf}.effectiveRate` as never, effectiveRate as never, {
              shouldDirty: false,
              shouldValidate: false,
            });
          };

          const mergedProjects = (newMember.memberProjects ?? []).map(newProject => {
            const oldProject = oldProjectById.get(newProject.id);
            if (!oldProject) return newProject;
            const leaf = `${base}.project.p${newProject.id}`;
            const merged = mergeProject(newProject, oldProject, readLeaf(leaf));
            writeLeaf(leaf, merged.hours, merged.effectiveRate);
            return merged;
          });

          const mergedTimeOffs = (newMember.memberTimeOffs ?? []).map(newTimeOff => {
            const oldTimeOff = oldTimeOffById.get(newTimeOff.timeOffTypeId);
            if (!oldTimeOff) return newTimeOff;
            const leaf = `${base}.timeOff.t${newTimeOff.timeOffTypeId}`;
            const merged = mergeTimeOff(newTimeOff, oldTimeOff, readLeaf(leaf));
            writeLeaf(leaf, merged.hours, merged.effectiveRate);
            return merged;
          });

          return {
            ...newMember,
            memberProjects: mergedProjects,
            memberTimeOffs: mergedTimeOffs,
            memberSubRows: [...mergedProjects, ...mergedTimeOffs] as GenerateBillSubRow[],
          };
        });

        queryClient.setQueryData<InfiniteData<PaginatedResponse<GenerateBillListItem>>>(
          authoritativeQueryKey,
          () => ({
            pages: [
              new PaginatedResponse({
                items: mergedItems,
                pagination: { total: mergedItems.length, cursor: null },
              }),
            ],
            pageParams: [undefined],
          })
        );

        void table;

        // See invoices hook for the rationale on hashing fresh, not merged.
        markAccepted(fresh);
      } finally {
        setIsRefreshing(false);
      }
    },
    [authoritativeQueryKey, latestPeekRef, markAccepted, queryClient]
  );

  return useMemo(
    () => ({ hasStaleData, isRefreshing, applySmartRefresh }),
    [hasStaleData, isRefreshing, applySmartRefresh]
  );
};
